/**
 * Responsibility: Shared infrastructure for game state and lifecycle management.
 * Scope: Core concerns; used by both ui and engine layers.
 */
import type {
  GameState,
  AttributeKey,
  CombatState,
  NarratorAIResponse,
  Suggestion,
  LogicUpdate,
  LogicForgeMeta,
  InventoryItem,
  Companion,
  ActionEnvelope,
  ActionMode,
  ActionSource,
  ActionTargetRef,
  ActionToolRef,
  ImportedWorldData,
  ImportedPlayerData,
  WorldGalleryEntry,
  VesselGalleryEntry,
} from "@anyquest/shared-core"
import { CombatStatus, GameOrigin, ActionCategory, QUEST_VALID_TRANSITIONS } from "@anyquest/shared-core"
import { buildActionCacheKey, buildActionEnvelope, buildActionExecutionContext } from './engine/action-contract'
import { applyDueScheduledEffectJobs, defaultGameState, flatStatsToAttributes, normalizeSkills, normalizeAttributeMetadata, normalizeSex, generateNpcId } from './engine/state'
import { buildPortraitPrompt, normalizeGenesisIdentity, resolveCreationSex } from './engine/character-creation'
import { advanceClock, formatClock, totalMinFromClock } from './engine/temporal'
import { reconcileSuccessLevelWithCheck } from './engine/action-resolution'
import { fullVitals, normalizeVitals } from './engine/vitals'
import type {
  SharedPlayerProfile,
  SharedStarterKit,
  SaveMeta,
} from './engine/persist'
import {
  runGenesis, runActionValidator, runLogicPhase, runCheatCommand, streamNarratorPhase, runNarratorPhase,
  runTraitShift, runCompanionChat, abilityForge, executeInWorker, FALLBACK_LOGIC, FALLBACK_META,
  runWorldForge,
} from './ai/logicForge'
import { loadAIConfig } from './ai/config'
import { formatAIErrorMessage } from './ai/errors'
import { getAppImportMetaEnv } from "@anyquest/shared-core"
import { generateImage, getImageGenerationStatus } from './ai/imagen'
import { tryStandardAction } from './engine/standard-actions'
import {
  startCombat, processPlayerTurn, processNpcTurn,
  fleeCombat, isFlee, isAttackAction, pickOpponent, extractTargetName,
  setCombatTarget, getActiveTargetName, listCombatFoes,
  peekNextCombatActor, advanceCombatQueueCursor, shouldNpcActFirst,
} from './engine/combat'
import { getCompanionActions } from './engine/autonomy'
import { generateWorldTaxonomy } from './engine/world-taxonomy'
import {
  startWorldTick, stopWorldTick, setWorldSimState, setWorldSimCallbacks,
  seedWorldNPCs, scheduleEnrichmentIfNeeded, checkAndTick, syncRoomNPCs,
  processTravelRisk
} from './engine/world-sim'
import { playEffect, playAmbient, inferAmbientScene, setAmbientSignature, setScoreMood, inferScoreMood, registerDynamicScore, clearDynamicScores } from './engine/audio'
import { speakNarration } from './engine/tts'
import { isMetaSystemNpcName } from './engine/npc-guard'
import { dispatcher, isDevMode } from './app-states'
import { TOPICS } from "@anyquest/shared-core"
import { authStore } from './ui/stores/auth-store'
import {
  hasEntropyTrait as _hasEntropyTrait,
  asAttributeKey,
  parseAbilityInvocation,
  getResolvedAbilityArgs,
  getActionSkillContext,
  resolveMechanicsBonus,
  inferMovementFallback,
  buildStarterAtlasScaffold,
} from './ui/stores/game-store-helpers'
import { applyLogicUpdates, applyNpcResult } from './ui/stores/game-store-mutations'
import { applyAtlasKnowledge } from './ui/helpers/atlas-knowledge'
export { getResolvedAbilityArgs } from './ui/stores/game-store-helpers'

// ============================================================
// GAME-STORE.TS — Game state singleton + action loop
// handleAction orchestrates: Logic Forge → combat resolution
// → Narrator (with combat context) → state persistence.
// ============================================================

export let state: GameState | null = null
export let activeSaveId: string | null = null

/** Transient state for active negotiations (to persist through dialog nesting) */
export let negotiation = {
  playerOffer: [] as InventoryItem[],
  npcSelected: [] as InventoryItem[],
  npcOffer: null as { name: string, worth: number } | null,
}

export function resetNegotiation() {
  negotiation.playerOffer = []
  negotiation.npcSelected = []
  negotiation.npcOffer = null
}
export let isProcessing = false
export let importedWorldData: ImportedWorldData | null = null
export let importedPlayerData: ImportedPlayerData | null = null
let actionSeq = 0
let lastSavedFingerprint: string | null = null
let persistRuntimePromise: Promise<typeof import('./engine/persist')> | null = null
let logicCacheRuntimePromise: Promise<typeof import('./engine/logic-cache')> | null = null
let npcLogicCacheRuntimePromise: Promise<typeof import('./engine/npc-logic-cache')> | null = null
const MAX_GENESIS_COMPANION_DEPTH = 4
const MAX_GENESIS_COMPANION_NODES = 24

function loadPersistRuntime() {
  if (!persistRuntimePromise) persistRuntimePromise = import('./engine/persist')
  return persistRuntimePromise
}

function loadLogicCacheRuntime() {
  if (!logicCacheRuntimePromise) logicCacheRuntimePromise = import('./engine/logic-cache')
  return logicCacheRuntimePromise
}

function loadNpcLogicCacheRuntime() {
  if (!npcLogicCacheRuntimePromise) npcLogicCacheRuntimePromise = import('./engine/npc-logic-cache')
  return npcLogicCacheRuntimePromise
}

interface PendingAction {
  input: string
  companionName?: string
  actorKind: 'player' | 'companion'
  actorName?: string
  actionEnvelope: ActionEnvelope
  logicState: GameState
  logicFn: string
  meta: LogicForgeMeta
  skillContext?: {
    name: string
    level: number
    bonus: number
  }
  mechanics?: {
    dc: number
    primaryStat: string
    bonus: number
    chance: number
    bonusSummary?: string
    bonusDetail?: string
  }
  actionId: number
}
let pendingAction: PendingAction | null = null
type HandleActionOptions = {
  source?: ActionSource
  mode?: ActionMode
  category?: ActionCategory
  tool?: ActionToolRef
  explicitTargets?: ActionTargetRef[]
  eventId?: string
  eventTitle?: string
  tags?: string[]
}
type CombatLogicCacheEntry = {
  logicFn: string
  meta: LogicForgeMeta
  mechanics?: PendingAction['mechanics']
}
const combatLogicCache = new Map<string, CombatLogicCacheEntry>()
function clearCombatLogicCache(): void {
  combatLogicCache.clear()
}

function combatLogicCacheKey(
  gs: GameState,
  input: string,
  actionActor: ReturnType<typeof resolveActionActor>,
  abilityInvocation: ReturnType<typeof parseAbilityInvocation> | null,
  options: HandleActionOptions = {},
): string {
  const actionEnvelope = buildActionEnvelope(gs, input, {
    source: options.source ?? (actionActor.kind === 'companion' ? 'companion' : 'player'),
    mode: options.mode,
    actor: actionActor.kind === 'companion'
      ? { kind: 'companion', name: actionActor.name }
      : { kind: 'player' },
    category: options.category,
    ability: abilityInvocation?.ability,
    argsText: abilityInvocation?.argsText,
    tool: options.tool,
    explicitTargets: options.explicitTargets,
    eventId: options.eventId,
    eventTitle: options.eventTitle,
    tags: options.tags,
  })
  return buildActionCacheKey(gs, actionEnvelope)
}

function resolveActionActor(
  gs: GameState,
  companionName?: string,
): { kind: 'player'; entity: GameState['player']; name?: undefined } | { kind: 'companion'; entity: GameState['companions'][number]; name: string } {
  if (companionName) {
    const targetName = String(companionName).trim().toLowerCase()
    const companion = (gs.companions ?? []).find(c => c.name.toLowerCase() === targetName)
    if (companion) return { kind: 'companion', entity: companion, name: companion.name }
  }
  return { kind: 'player', entity: gs.player }
}

function projectLogicStateForActor(
  gs: GameState,
  actor: ReturnType<typeof resolveActionActor>,
): GameState {
  if (actor.kind === 'player') return gs
  return {
    ...gs,
    player: {
      ...actor.entity,
      name: actor.entity.name,
      location: { ...gs.player.location },
    },
  }
}

function buildPendingActionEnvelope(
  gs: GameState,
  input: string,
  actionActor: ReturnType<typeof resolveActionActor>,
  meta: LogicForgeMeta,
  abilityInvocation: ReturnType<typeof parseAbilityInvocation> | null,
  options: HandleActionOptions = {},
): ActionEnvelope {
  return buildActionEnvelope(gs, input, {
    source: options.source ?? (actionActor.kind === 'companion' ? 'companion' : 'player'),
    mode: options.mode,
    actor: actionActor.kind === 'companion'
      ? { kind: 'companion', name: actionActor.name }
      : { kind: 'player' },
    category: options.category ?? meta.category,
    ability: abilityInvocation?.ability,
    argsText: abilityInvocation?.argsText,
    tool: options.tool,
    explicitTargets: options.explicitTargets,
    eventId: options.eventId,
    eventTitle: options.eventTitle,
    tags: options.tags,
  })
}

// Handle manual dice rolls from the UI wizard
dispatcher.on(TOPICS.ui.diceRolled, (_e, data: { roll: number }) => {
  if (pendingAction) {
    const action = pendingAction
    pendingAction = null
    completeAction(action, data.roll).catch(e => {
      console.error('[Action] Resolution failed:', e)
      isProcessing = false
      save()
    })
  }
})
// Inject save + isProcessing getter into world-sim to avoid circular imports
setWorldSimCallbacks(
  () => save(),
  () => isProcessing,
)
function uid(): string | undefined { return authStore.user?.uid ?? undefined; }
const LOCAL_SESSION_OWNER = '__local_session__'

async function getSaveOwnerCandidates(preferredUid?: string): Promise<string[]> {
  const candidates: string[] = []
  const normalizedUid = String(preferredUid ?? '').trim()
  if (normalizedUid) candidates.push(normalizedUid)
  const { getPersistenceMode } = await loadPersistRuntime()
  const persistenceMode = getPersistenceMode()
  if (persistenceMode === 'local-only' || !normalizedUid) {
    candidates.push(LOCAL_SESSION_OWNER)
  }
  return [...new Set(candidates.filter(Boolean))]
}

async function getSaveWriteOwner(preferredUid?: string): Promise<string> {
  const normalizedUid = String(preferredUid ?? '').trim()
  if (normalizedUid) return normalizedUid
  const { getPersistenceMode } = await loadPersistRuntime()
  return getPersistenceMode() === 'local-only' ? LOCAL_SESSION_OWNER : ''
}

function mergeSaveLists(lists: SaveMeta[][]): SaveMeta[] {
  const merged = new Map<string, SaveMeta>()
  for (const list of lists) {
    for (const save of list) {
      const existing = merged.get(save.id)
      if (!existing || (save.state.meta.timestamp ?? 0) >= (existing.state.meta.timestamp ?? 0)) {
        merged.set(save.id, save)
      }
    }
  }
  return [...merged.values()].sort((a, b) => (b.state.meta.timestamp ?? 0) - (a.state.meta.timestamp ?? 0))
}

async function listAccessibleSaves(preferredUid?: string): Promise<SaveMeta[]> {
  const owners = await getSaveOwnerCandidates(preferredUid)
  if (!owners.length) return []
  const { listSaves } = await loadPersistRuntime()
  const lists = await Promise.all(owners.map((owner) => listSaves(owner)))
  return mergeSaveLists(lists)
}

async function findSaveOwner(saveId: string, preferredUid?: string): Promise<string | null> {
  const normalizedSaveId = String(saveId ?? '').trim()
  if (!normalizedSaveId) return null
  const owners = await getSaveOwnerCandidates(preferredUid)
  if (!owners.length) return null
  const { loadGame } = await loadPersistRuntime()
  for (const owner of owners) {
    const loaded = await loadGame(owner, normalizedSaveId)
    if (loaded) return owner
  }
  return null
}

// Centralized dispatch wrapper keeps call sites terse and consistent.
function notify(topic: string, data?: unknown): void {
  dispatcher.trigger('game-store', topic, data)
}

function updateScoreMood(gs: GameState): void {
  const vitals = gs.player?.vitals
  const maxV = vitals?.maxVitality || 100
  setScoreMood(inferScoreMood({
    inCombat: !!gs.combat,
    lowHealth: vitals ? vitals.vitality / maxV < 0.25 : false,
    newRoom: false,
  }))
}

// --- Dynamic AI Music Generation ---
let musicForgeActive = false

async function loadMusicForgeRuntime() {
  return import('./ai/musicForge')
}

async function loadMusicCacheRuntime() {
  return import('./engine/music-cache')
}

function triggerMusicGeneration(worldSeed: string, sceneDesc: string): void {
  if (musicForgeActive) return
  musicForgeActive = true

  Promise.resolve().then(async () => {
    const [{ generateMusicScore }, { getCachedScore, cacheScore }] = await Promise.all([
      loadMusicForgeRuntime(),
      loadMusicCacheRuntime(),
    ])

    const keywords = `${worldSeed} ${sceneDesc}`.toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(w => w.length >= 3).slice(0, 20)
    const moods: Array<import('./engine/audio').ScoreMood> = ['calm', 'tension', 'combat', 'discovery', 'triumph', 'sorrow']

    for (const mood of moods) {
      const cached = await getCachedScore(mood, keywords)
      if (cached) {
        registerDynamicScore(mood, cached.notes as any[], cached.durationMs)
      }
    }

    for (const mood of moods) {
      generateMusicScore(mood, sceneDesc, worldSeed).then(async score => {
        if (!score) return
        registerDynamicScore(mood, score.notes as any[], score.durationMs)
        await cacheScore(score)
      }).catch(e => console.warn(`[MusicForge] ${mood} generation failed:`, e))
    }
  }).catch(e => {
    console.warn('[MusicForge] Init failed:', e)
  }).finally(() => {
    musicForgeActive = false
  })
}

function triggerSceneMusicRefresh(worldSeed: string, sceneDesc: string): void {
  Promise.resolve().then(async () => {
    const [{ generateMusicScore }, { getCachedScore, cacheScore }] = await Promise.all([
      loadMusicForgeRuntime(),
      loadMusicCacheRuntime(),
    ])

    const gs = state
    if (!gs) return
    const mood = inferScoreMood({
      inCombat: !!gs.combat,
      lowHealth: gs.player?.vitals ? gs.player.vitals.vitality / (gs.player.vitals.maxVitality || 100) < 0.25 : false,
      newRoom: true,
    })

    const keywords = `${worldSeed} ${sceneDesc}`.toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(w => w.length >= 3).slice(0, 20)

    const cached = await getCachedScore(mood, keywords)
    if (cached) {
      registerDynamicScore(mood, cached.notes as any[], cached.durationMs)
      return
    }

    const score = await generateMusicScore(mood, sceneDesc, worldSeed)
    if (score) {
      registerDynamicScore(mood, score.notes as any[], score.durationMs)
      await cacheScore(score)
    }
  }).catch(e => console.warn('[MusicForge] Scene refresh failed:', e))
}

function sanitizeRoomNpcs(
  npcs: GameState['currentRoom']['npcs'] | undefined,
): GameState['currentRoom']['npcs'] {
  const list = Array.isArray(npcs) ? npcs : []
  const seen = new Set<string>()
  const next: GameState['currentRoom']['npcs'] = []
  for (const raw of list) {
    const name = String(raw?.name ?? '').trim()
    const identity = String(raw?.identity ?? '').trim()
    if (!name || !identity) continue
    if (isMetaSystemNpcName(name)) continue
    const key = name.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    next.push({
      ...raw,
      name,
      identity,
      sex: normalizeSex(raw?.sex),
      inventory: Array.isArray(raw?.inventory)
        ? raw.inventory.map(item => String(item ?? '').trim()).filter(Boolean)
        : [],
    })
  }
  return next
}

function sanitizeWorldNpcs(
  npcs: GameState['world']['npcs'] | undefined,
): GameState['world']['npcs'] {
  const list = Array.isArray(npcs) ? npcs : []
  const seen = new Set<string>()
  const next: GameState['world']['npcs'] = []
  for (const raw of list) {
    const name = String(raw?.name ?? '').trim()
    if (!name) continue
    if (isMetaSystemNpcName(name)) continue
    const key = String(raw?.id ?? '').trim() || name.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    next.push({
      ...raw,
      name,
      sex: normalizeSex(raw?.sex),
      inventory: Array.isArray(raw?.inventory)
        ? raw.inventory.map(item => String(item ?? '').trim()).filter(Boolean)
        : [],
    })
  }
  return next
}

export function clearImportedWorldData(): void {
  importedWorldData = null
  importedPlayerData = null
}

export function normalizeImportedWorldData(data: ImportedWorldData | null | undefined): ImportedWorldData | null {
  const seed = String(data?.seed ?? '').trim()
  const atlas = data?.atlas
  const room = data?.room
  if (!seed || !atlas || !room) return null
  return {
    seed,
    atlas: structuredClone(atlas),
    room: structuredClone(room),
  }
}

export function setImportedWorldData(data: ImportedWorldData | null): void {
  importedWorldData = normalizeImportedWorldData(data)
}

export function setImportedPlayerData(data: ImportedPlayerData | null): void {
  importedPlayerData = data
}

export function selectCombatTarget(targetName: string): void {
  if (!state?.combat || state.combat.status !== CombatStatus.active) return
  const next = setCombatTarget(state.combat, targetName)
  if (next === state.combat) return
  state.combat = next
  notify(TOPICS.combat.targetChanged, {
    targetName: getActiveTargetName(next),
    foes: listCombatFoes(next),
  })
}

export function sysLog(msg: string): void {
  notify(TOPICS.ui.notify, { message: `[${new Date().toLocaleTimeString()}] ${msg}`, level: 'info' })
}

function cacheVisionOnCurrentNode(gs: GameState, url: string): void {
  const locKey = `${gs.player.location.x}_${gs.player.location.y}_${gs.player.location.z ?? 0}`
  const node = gs.atlas.nodes[locKey]
  if (node) node.visionUrl = url
}

function buildVisionPrompt(gs: GameState, room: { name?: string; desc?: string }): string {
  const name = room.name?.trim() || 'Unknown Place'
  const desc = room.desc?.trim() || 'A mysterious location with uncertain details.'
  const setting = gs.world?.taxonomy?.imageStyle || gs.world?.seed || 'Atmospheric fantasy environment'
  return `Concept art of a location named "${name}". Scene details: ${desc}. Setting: ${setting}. Wide shot, immersive lighting, no text, no UI.`
}

const imageWarnedByKind = new Set<string>()
const appEnv = getAppImportMetaEnv()

function hasImageProviderConfigured(): boolean {
  const cfg = loadAIConfig()
  return !!(
    String(cfg.geminiApiKey ?? '').trim()
    || String(cfg.geminiProxyUrl ?? '').trim()
    || String(appEnv.VITE_GEMINI_API_KEY ?? '').trim()
    || String(appEnv.VITE_GEMINI_PROXY_URL ?? '').trim()
  )
}

function warnImageUnavailable(kind: 'vision' | 'portrait' | 'companion-portrait'): void {
  if (imageWarnedByKind.has(kind)) return
  imageWarnedByKind.add(kind)
  if (getImageGenerationStatus() === 'quota_cooldown') return
  const message = kind === 'vision'
    ? 'Scene image generation unavailable. Add Gemini key/proxy in AI settings.'
    : kind === 'portrait'
      ? 'Player portrait generation unavailable. Add Gemini key/proxy in AI settings.'
      : 'Companion portrait generation unavailable. Add Gemini key/proxy in AI settings.'
  notify(TOPICS.ui.notify, { message, level: 'warn' })
}

function requestImageWithRetry(
  kind: 'vision' | 'portrait' | 'companion-portrait',
  prompt: string,
  onSuccess: (url: string) => void,
): void {
  if (!hasImageProviderConfigured()) {
    warnImageUnavailable(kind)
    return
  }

  generateImage(prompt)
    .then(url => {
      if (url) {
        onSuccess(url)
        return
      }
      warnImageUnavailable(kind)
    })
    .catch(() => {
      warnImageUnavailable(kind)
    })
}

function normalizeNotebookText(text: string): string {
  return text
    .replace(/\s+/g, ' ')
    .replace(/\*+/g, '')
    .replace(/^\[[A-Z]+\]\s*/i, '')
    .trim()
}

type NotebookTopic = 'world' | 'location' | 'people' | 'event' | 'quest' | 'atlas'

const NOTEBOOK_TOPIC_TAG: Record<NotebookTopic, string> = {
  world: 'WORLD',
  location: 'LOCATION',
  people: 'PEOPLE',
  event: 'EVENT',
  quest: 'QUEST',
  atlas: 'ATLAS',
}

function clampNotebookText(text: string): string {
  const firstSentence = String(text ?? '').match(/^(.+?[.!?])(?:\s|$)/)?.[1] ?? text
  const oneLine = normalizeNotebookText(firstSentence)
  return oneLine.length <= 180 ? oneLine : `${oneLine.slice(0, 177)}...`
}

function isLowSignalNotebookText(text: string): boolean {
  const lower = text.toLowerCase()
  if (lower.length < 18) return true
  if (/^(you|i)\s+(look|check|wait|pause|stand|walk|observe)\b/.test(lower)) return true
  if (/^(nothing|no change|still|same)\b/.test(lower)) return true
  return false
}

function shouldCaptureNarrationNotebook(text: string): boolean {
  const clean = clampNotebookText(text)
  if (!clean) return false
  if (!isLowSignalNotebookText(clean)) return true
  return /\b(discover|learn|unlock|wound|bleed|warn|reveal|quest|map|ally|enemy|portal|artifact)\b/i.test(clean)
}

function addNotebookEntry(gs: GameState, text: string, topic: NotebookTopic = 'event'): void {
  const clean = clampNotebookText(String(text ?? ''))
  if (!clean) return
  if (isLowSignalNotebookText(clean) && topic === 'event') return
  if (!Array.isArray(gs.notebook)) gs.notebook = []

  const tagged = `[${NOTEBOOK_TOPIC_TAG[topic]}] ${clean}`
  const key = `${topic}|${clean.toLowerCase()}`
  const existing = gs.notebook.findIndex(entry => {
    const raw = String(entry ?? '')
    const tagMatch = raw.match(/^\[([A-Z]+)\]\s*/)
    const existingTopic = tagMatch ? String(tagMatch[1]).toLowerCase() : 'event'
    const body = normalizeNotebookText(raw).toLowerCase()
    return `${existingTopic}|${body}` === key
  })
  if (existing === 0) return
  if (existing > 0) gs.notebook.splice(existing, 1)

  gs.notebook.unshift(tagged)
  if (gs.notebook.length > 160) gs.notebook = gs.notebook.slice(0, 160)
}

function normalizeImportedPlayerData(raw: unknown): ImportedPlayerData | null {
  if (!raw || typeof raw !== 'object') return null
  const src = raw as Omit<Partial<ImportedPlayerData>, 'vitals' | 'inventory'> & {
    vitals?: Partial<ImportedPlayerData['vitals']>
    inventory?: Array<ImportedPlayerData['inventory'][number] | string>
  }
  const name = String(src.name ?? '').trim()
  const identity = String(src.identity ?? '').trim()
  const bio = String(src.bio ?? '').trim()
  if (!identity || !bio) return null

  const attributes = normalizeAttributeMetadata(src.attributes ?? {})
  const skills = normalizeSkills(src.skills, attributes)
  const vitals = normalizeVitals(src.vitals, attributes)
  const inventory = (src.inventory ?? [])
    .map(item => {
      if (typeof item === 'string') {
        const name = item.trim()
        return name ? { name, equipped: false } : null
      }
      const name = String(item?.name ?? '').trim()
      if (!name) return null
      return {
        name,
        equipped: !!item?.equipped,
        ...(item?.desc ? { desc: String(item.desc).trim() } : {}),
        ...(item?.icon ? { icon: String(item.icon).trim() } : {}),
        ...(typeof item?.worth === 'number' ? { worth: item.worth } : {}),
        ...(Array.isArray((item as any)?.actions) ? { actions: (item as any).actions } : {}),
      }
    })
    .filter(Boolean) as ImportedPlayerData['inventory']

  return {
    name: name || undefined,
    identity,
    bio,
    sex: normalizeSex(src.sex),
    vitals,
    attributes,
    skills,
    abilities: structuredClone(Array.isArray(src.abilities) ? src.abilities : []),
    traits: structuredClone(Array.isArray(src.traits) ? src.traits : []),
    inventory,
  }
}

function queueCompanionPortraitGeneration(companions: Array<{ name: string; identity: string; sex?: string; bio: string }>, delayStepMs = 450): void {
  companions.forEach((c, idx) => {
    const scheduleMs = Math.max(0, idx * delayStepMs)
    const run = (): void => {
      if (!state) return
      requestImageWithRetry(
        'companion-portrait',
        buildPortraitPrompt({ name: c.name, identity: c.identity, sex: c.sex, bio: c.bio }),
        (p) => {
          if (!state) return
          const comp = state.companions.find(x => x.name === c.name)
          if (comp) {
            comp.portraitUrl = p
            notify(TOPICS.game.imageReady, { url: p, kind: 'companion-portrait', name: c.name })
          }
        },
      )
    }
    if (scheduleMs === 0) run()
    else setTimeout(run, scheduleMs)
  })
}

// --- Load ---

export async function loadGameById(userId: string, id: string): Promise<GameState> {
  sysLog('Restoring reality...')
  const [{ loadGame, startPendingSaveSync }, { initLogicCache }, { initNpcLogicCache }] = await Promise.all([
    loadPersistRuntime(),
    loadLogicCacheRuntime(),
    loadNpcLogicCacheRuntime(),
  ])
  const owners = await getSaveOwnerCandidates(userId)
  let loaded: GameState | null = null
  let resolvedOwner = ''
  for (const owner of owners) {
    loaded = await loadGame(owner, id)
    if (loaded) {
      resolvedOwner = owner
      break
    }
  }
  if (!loaded) throw new Error('Save not found')
  pendingAction = null
  clearCombatLogicCache()
  isProcessing = false
  state = loaded
  state.notebook = Array.isArray(state.notebook) ? state.notebook : []
  state.player.sex = normalizeSex(state.player.sex)
  state.player.dormantEffects = state.player.dormantEffects ?? []
  state.player.skills = normalizeSkills(state.player.skills, state.player.attributes)
  state.companions = (state.companions ?? []).map(c => ({
    ...c,
    sex: normalizeSex(c.sex),
    dormantEffects: c.dormantEffects ?? [],
    skills: normalizeSkills(c.skills, c.attributes),
  }))
  state.currentRoom.npcs = sanitizeRoomNpcs(state.currentRoom.npcs)
  state.world.npcs = sanitizeWorldNpcs(state.world.npcs)
  state.currentRoom.dormantFlags = state.currentRoom.dormantFlags ?? []
  state.combat = null; // never restore mid-combat
  if (!state.world.taxonomy && state.world.seed) {
    generateWorldTaxonomy(state.world.seed).then(t => { if (state) state.world.taxonomy = t; })
  }
  activeSaveId = id
  lastSavedFingerprint = buildSaveFingerprint(state)
  const runtimeOwner = resolvedOwner || userId
  if (runtimeOwner && runtimeOwner !== LOCAL_SESSION_OWNER) {
    initLogicCache(runtimeOwner, id).catch(console.warn)
    initNpcLogicCache(runtimeOwner)
    startPendingSaveSync(runtimeOwner)
  }
  setWorldSimState(state)
  startWorldTick()
  scheduleEnrichmentIfNeeded()
  setAmbientSignature(state.world?.seed || '', state.currentRoom.desc || state.currentRoom.name || '')
  playAmbient(inferAmbientScene(state.currentRoom.desc || state.currentRoom.name))
  updateScoreMood(state)
  notify(TOPICS.game.loaded, state)
  clearDynamicScores()
  triggerMusicGeneration(state.world?.seed || '', state.currentRoom.desc || state.currentRoom.name || '')

  sysLog('Reality restored.')
  if (!state.visionUrl && (state.currentRoom.desc || state.currentRoom.name)) {
    requestImageWithRetry(
      'vision',
      buildVisionPrompt(state, state.currentRoom),
      (v) => {
        if (!state) return
        state.visionUrl = v
        cacheVisionOnCurrentNode(state, v)
        notify(TOPICS.game.imageReady, { url: v, kind: 'vision' })
      },
    )
  }
  if (!state.portraitUrl && state.player.bio) {
    requestImageWithRetry(
      'portrait',
      buildPortraitPrompt({
        name: state.player.name,
        identity: state.player.identity,
        sex: state.player.sex,
        bio: state.player.bio,
      }),
      (p) => {
        if (!state) return
        state.portraitUrl = p
        notify(TOPICS.game.imageReady, { url: p, kind: 'portrait' })
      },
    )
  }
  const missingCompanionPortraits = (state.companions ?? [])
    .filter(c => !c.portraitUrl && !!c.bio)
    .map(c => ({ name: c.name, identity: c.identity, sex: c.sex, bio: c.bio }))
  queueCompanionPortraitGeneration(missingCompanionPortraits)
  return state
}

// --- Save (Debounced) ---

let saveTimeout: ReturnType<typeof setTimeout> | null = null
const SAVE_DEBOUNCE_MS = 2000
function flushPendingSaveOnPageExit(): void {
  if (!saveTimeout) return
  clearTimeout(saveTimeout)
  saveTimeout = null
  void save(true)
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushPendingSaveOnPageExit)
  document.addEventListener('visibilitychange', () => {
    document.visibilityState === 'hidden' && flushPendingSaveOnPageExit()
  })
}

function buildSaveFingerprint(gs: GameState): string {
  return JSON.stringify({
    ...gs,
    meta: {
      ...(gs.meta ?? {}),
      timestamp: 0,
    },
  })
}

export async function save(immediate = false): Promise<void> {
  if (saveTimeout) clearTimeout(saveTimeout)
  if (immediate) {
    await performSave(true)
  } else {
    saveTimeout = setTimeout(() => performSave(false), SAVE_DEBOUNCE_MS)
  }
}

async function performSave(force = false): Promise<void> {
  saveTimeout = null
  if (!activeSaveId || !state) return
  const { saveGame } = await loadPersistRuntime()
  const resolvedUid = uid()
  const saveOwnerId = await getSaveWriteOwner(resolvedUid)
  if (!saveOwnerId) return
  const fingerprint = buildSaveFingerprint(state)
  if (!force && fingerprint === lastSavedFingerprint) return
  state.meta.timestamp = Date.now()
  try {
    await saveGame(saveOwnerId, activeSaveId, state)
    lastSavedFingerprint = fingerprint
    notify(TOPICS.game.saved, { saveId: activeSaveId })
  } catch (e) {
    sysLog(`⚠ Save failed: ${String(e).slice(0, 120)}`)
    console.error('[save]', e)
  }
}

// --- Main action loop ---

export async function handleAction(input: string, companionName?: string, options: HandleActionOptions = {}): Promise<void> {
  const isCompanionAction = !!companionName
  if (isProcessing) { console.warn('[Action] BLOCKED: already processing'); return; }
  if (!input.trim()) { console.warn('[Action] BLOCKED: empty input'); return; }
  if (!state) { console.warn('[Action] BLOCKED: no game state'); return; }

  if (!isCompanionAction && state.player.vitals && state.player.vitals.vitality <= 0) {
    notify(TOPICS.ui.dialogOpen, {
      content: 'info-dialog',
      data: {
        title: 'YOU HAVE FALLEN',
        desc: 'Your vitality has reached zero. The adventure ends here. You can load a previous save from the Hub.',
        meta: 'GAME OVER',
      },
      options: { variant: 'danger', showFooter: true, showCloseButton: true }
    })
    return
  }

  const actionId = ++actionSeq
  isProcessing = true
  notify(TOPICS.game.actionStart, { input, actionId, isCompanionAction })
  try {
    const gs = state
    const actionActor = resolveActionActor(gs, companionName)
    const actionLogicState = projectLogicStateForActor(gs, actionActor)
    const primeAbilityCooldown = (abilityName: string) => {
      const wanted = String(abilityName || '').trim().toLowerCase()
      if (!wanted) return
      const owner = actionActor.kind === 'companion'
        ? (gs.companions ?? []).find(c => c.name === actionActor.name)
        : gs.player
      const ability = owner?.abilities?.find(entry => String(entry?.name || '').trim().toLowerCase() === wanted)
      if (!ability) return
      ability.lastUsed = totalMinFromClock(gs.world.clock)
      notify(TOPICS.game.actionUpdate, { state: gs, actionId, isCompanionAction, abilityPrimed: true })
    }
    const cheatMatch = !isCompanionAction && isDevMode() && /^cheat:\s*(.+)/i.exec(input.trim())
    if (cheatMatch) {
      const cheatPrompt = cheatMatch[1].trim()
      sysLog(`[DEV] Cheat: "${cheatPrompt.slice(0, 60)}"`)
      const cheatLogicUpdate = await runCheatCommand(cheatPrompt, gs)
      const cheatAction: PendingAction = {
        input: `[DEV] ${cheatPrompt}`,
        companionName: undefined,
        actorKind: 'player',
        actorName: undefined,
        actionEnvelope: buildActionEnvelope(gs, `[DEV] ${cheatPrompt}`, {
          actor: { kind: 'player' },
          category: ActionCategory.other,
          tags: ['dev_cheat'],
        }),
        logicState: gs,
        logicFn: '',
        meta: { cacheable: false, category: ActionCategory.other },
        mechanics: undefined,
        skillContext: undefined,
        actionId,
      }
      await completeAction(cheatAction, undefined, cheatLogicUpdate)
      return
    }

    const abilityInvocation = parseAbilityInvocation(actionLogicState, input)
    const isAbility = !!abilityInvocation
    const isStandard = !isAbility && tryStandardAction(actionLogicState, input) !== null
    if (!isCompanionAction && !isStandard && !isAbility) {
      const { verdict } = await runActionValidator(gs, input)
      if (verdict === 'bad') {
        sysLog("That action isn't possible right now.")
        isProcessing = false
        notify(TOPICS.game.actionComplete, { state: gs, actionId, isCompanionAction })
        const badActionEntry = {
          location: gs.currentRoom.name,
          time: formatClock(gs.world.clock),
          text: `**${gs.player.name || gs.player.identity?.split(/\s+/).slice(0, 2).join(' ') || 'Player'}**: ${input}\n\nThat action isn't possible right now. The world does not respond.`,
          suggestions: gs.history[gs.history.length - 1]?.suggestions ?? [],
          context: { items: [...gs.currentRoom.items], npcs: [...gs.currentRoom.npcs] },
        }
        gs.history.push(badActionEntry)
        notify(TOPICS.game.actionUpdate, { state: gs })
        return
      }
    }

    let logicFnString = '(state, roll) => ({ successLevel: 0, updates: { timeCost: 5 } })'
    let meta: LogicForgeMeta = FALLBACK_META
    let mechanics: PendingAction['mechanics']
    let logicUpdateFromPhase: LogicUpdate | undefined
    const inActiveCombat = gs.combat?.status === CombatStatus.active
    const combatKey = inActiveCombat
      ? combatLogicCacheKey(actionLogicState, input, actionActor, abilityInvocation, options)
      : null
    const runLogicPhaseWithCombatReuse = async (forceAbilityCategory = false): Promise<void> => {
      if (combatKey) {
        const cached = combatLogicCache.get(combatKey)
        if (cached) {
          logicFnString = cached.logicFn
          meta = forceAbilityCategory
            ? { ...cached.meta, category: ActionCategory.ability }
            : cached.meta
          mechanics = cached.mechanics ? { ...cached.mechanics } : undefined
          logicUpdateFromPhase = undefined
          return
        }
      }

      const result = await runLogicPhase(
        actionLogicState,
        input,
        actionActor.kind === 'companion' ? actionActor.name : undefined,
        options,
      )
      logicUpdateFromPhase = result.logicUpdate
      logicFnString = result.logicFnString
      meta = forceAbilityCategory
        ? { ...result.meta, category: ActionCategory.ability }
        : result.meta
      mechanics = result.mechanics
      if (combatKey) {
        combatLogicCache.set(combatKey, {
          logicFn: logicFnString,
          meta,
          mechanics: mechanics ? { ...mechanics } : undefined,
        })
      }
    }
    if (abilityInvocation) {
      const ability = abilityInvocation.ability
      if (ability) {
        const resolvedArgs = getResolvedAbilityArgs(actionLogicState, ability)
        if (resolvedArgs.length && !abilityInvocation.argsText) {
          const targets = gs.currentRoom.npcs.map(n => n.name)
          targets.unshift(gs.player.identity)
          targets.unshift(...(gs.companions ?? []).map(c => c.name))
          dispatcher.trigger('game-store', TOPICS.ui.dialogOpen, {
            content: 'ability-args-dialog',
            data: {
              ability: { ...ability, args: resolvedArgs },
              targets,
            },
            options: {
              title: ability.name.toUpperCase(),
            },
          })
          isProcessing = false
          notify(TOPICS.game.actionComplete, { state: gs, actionId, awaitArgs: true, isCompanionAction })
          return
        }

        if (resolvedArgs.length && abilityInvocation.argsText) {
          primeAbilityCooldown(ability.name)
          await runLogicPhaseWithCombatReuse(true)
        } else {
          primeAbilityCooldown(ability.name)
          meta = { category: ActionCategory.ability, cacheable: false }
          sysLog(`Forging logic for ${ability.name}...`)
          logicFnString = await abilityForge(ability, actionLogicState)
        }
      } else {
        await runLogicPhaseWithCombatReuse()
      }
    } else if (isStandard) {
      const standard = tryStandardAction(actionLogicState, input)!
      logicFnString = standard.logicFnString
      meta = standard.meta
    } else {
      await runLogicPhaseWithCombatReuse()
    }

    const skillContext = getActionSkillContext(actionLogicState, input, meta.category)
    if (mechanics) {
      mechanics = resolveMechanicsBonus(actionLogicState, mechanics, skillContext?.bonus ?? 0, {
        category: meta.category,
        input,
      })
    }
    if (logicUpdateFromPhase && skillContext?.bonus) {
      logicUpdateFromPhase = undefined
    }

    const isAttack = meta.category === ActionCategory.attack
    const isCombatActive = gs.combat?.status === CombatStatus.active
    const isSkillCheck = mechanics && Number.isFinite(mechanics.dc) && mechanics.dc > 0 && !meta.cacheable
    if (mechanics && !isCompanionAction && (isAttack || isCombatActive || isSkillCheck)) {
      pendingAction = {
        input,
        companionName,
        actorKind: actionActor.kind,
        actorName: actionActor.kind === 'companion' ? actionActor.name : undefined,
        actionEnvelope: buildPendingActionEnvelope(gs, input, actionActor, meta, abilityInvocation, options),
        logicState: actionLogicState,
        logicFn: logicFnString,
        meta,
        mechanics,
        skillContext,
        actionId,
      }
      if (isAttack && (!gs.combat || gs.combat.status !== CombatStatus.active)) {
        if (gs.currentRoom.npcs.length > 0) {
          const opponent = pickOpponent(gs.currentRoom.npcs, extractTargetName(input))
          if (opponent) {
            gs.combat = startCombat(
              opponent,
              gs.player,
              (gs.companions ?? []).map(c => c.name),
              (gs.currentRoom.npcs ?? []).map(n => n.name),
            )
            clearCombatLogicCache()
            notify(TOPICS.combat.started, { npc: opponent })
          }
        }
        if (!gs.combat || gs.combat.status !== CombatStatus.active) {
          const resolvedAction: PendingAction = {
            input,
            companionName,
            actorKind: actionActor.kind,
            actorName: actionActor.kind === 'companion' ? actionActor.name : undefined,
            actionEnvelope: buildPendingActionEnvelope(gs, input, actionActor, meta, abilityInvocation, options),
            logicState: actionLogicState,
            logicFn: logicFnString,
            meta,
            mechanics,
            skillContext,
            actionId,
          }
          if (logicUpdateFromPhase) {
            await completeAction(resolvedAction, undefined, logicUpdateFromPhase)
          } else {
            const roll = Math.floor(Math.random() * 20) + 1
            await completeAction(resolvedAction, roll)
          }
          return
        }
      }

      dispatcher.trigger('game-store', TOPICS.ui.diceRollRequired, {
        title: input.toUpperCase(),
        ...mechanics,
        skillBonus: skillContext?.bonus ?? 0,
        skillName: skillContext?.name ?? '',
        bonusSummary: mechanics.bonusSummary ?? '',
        bonusDetail: mechanics.bonusDetail ?? '',
      })
      return
    }

    const resolvedAction: PendingAction = {
      input,
      companionName,
      actorKind: actionActor.kind,
      actorName: actionActor.kind === 'companion' ? actionActor.name : undefined,
      actionEnvelope: buildPendingActionEnvelope(gs, input, actionActor, meta, abilityInvocation, options),
      logicState: actionLogicState,
      logicFn: logicFnString,
      meta,
      mechanics,
      skillContext,
      actionId,
    }
    if (logicUpdateFromPhase) {
      await completeAction(resolvedAction, undefined, logicUpdateFromPhase)
    } else {
      const roll = Math.floor(Math.random() * 20) + 1
      await completeAction(resolvedAction, roll)
    }

  } catch (e) {
    console.error('[Action] handleAction failed:', e)
    pendingAction = null
    isProcessing = false
    save()
    notify(TOPICS.game.actionFailed, { error: formatAIErrorMessage(e), actionId, isCompanionAction })
  }
}

async function completeAction(action: PendingAction, roll?: number, precomputedLogicUpdate?: LogicUpdate): Promise<void> {
  const { input, companionName, logicFn, meta, actionId, logicState, actorKind, actorName: actionActorName } = action
  const isCompanionAction = !!companionName
  const gs = state!
  const actorSnapshot = logicState ?? gs
  const actionActorEntity = actorKind === 'companion'
    ? (gs.companions ?? []).find(c => c.name.toLowerCase() === String(actionActorName ?? companionName ?? '').trim().toLowerCase()) ?? gs.player
    : gs.player
  try {
    const effectiveRoll = roll ?? (Math.floor(Math.random() * 20) + 1)
    const skillBonus = action.skillContext?.bonus ?? 0
    const checkBonus = Number.isFinite(action.mechanics?.bonus)
      ? Number(action.mechanics?.bonus)
      : skillBonus
    const adjustedRoll = Math.max(1, Math.min(40, effectiveRoll + checkBonus))
    const executionContext = buildActionExecutionContext(gs, action.actionEnvelope, adjustedRoll, action.mechanics)
    let logicUpdate = precomputedLogicUpdate
      ?? await executeInWorker(logicFn, actorSnapshot, adjustedRoll, FALLBACK_LOGIC, false, action.mechanics, executionContext)
    logicUpdate = {
      ...logicUpdate,
      successLevel: reconcileSuccessLevelWithCheck(
        logicUpdate.successLevel,
        adjustedRoll,
        action.mechanics ? { dc: action.mechanics.dc } : undefined,
      ),
    }

    const isAttack = meta.category === ActionCategory.attack
    const parsedAbility = meta.category === ActionCategory.ability
      ? parseAbilityInvocation(actorSnapshot, input)?.ability
      : undefined
    const isOffensive = isAttack || (
      !!parsedAbility &&
      getResolvedAbilityArgs(actorSnapshot, parsedAbility).some(arg => arg.type === 'target')
    )
    let playerLogicApplied = false
    let playerTurnInterrupted = false
    let statVal = actionActorEntity.attributes.STR?.val ?? gs.player.attributes.STR.val
    const primaryStat = action.mechanics?.primaryStat
      ? asAttributeKey(action.mechanics.primaryStat)
      : null
    if (primaryStat && actionActorEntity.attributes[primaryStat]) {
      statVal = actionActorEntity.attributes[primaryStat].val
    }

    const runNpcTurn = async (): Promise<boolean> => {
      if (!gs.combat || gs.combat.status !== CombatStatus.active) return false
      notify(TOPICS.combat.npcTurnStart, { npc: { name: getActiveTargetName(gs.combat) } })
      await new Promise(r => setTimeout(r, 1200))

      const { combat: afterNpc, npcResult } = await processNpcTurn(gs.combat, gs)
      gs.combat = afterNpc
      applyNpcResult(gs, npcResult, { emit: notify })
      const npcTurnEntry = gs.combat.log[gs.combat.log.length - 1]
      const npcActorName = npcTurnEntry?.actorName || getActiveTargetName(gs.combat)
      notify(TOPICS.combat.turnEnd, {
        round: Math.max(1, gs.combat.round - 1),
        actor: 'npc',
        actorName: npcActorName,
        action: `${npcActorName} attacks`,
        outcome: npcResult.outcome,
        vitalityDelta: npcResult.vitalityDelta ?? 0,
        spiritDelta: npcResult.spiritDelta ?? 0,
        essenceDelta: npcResult.essenceDelta ?? 0,
      })
      if (gs.player.vitals.vitality <= 0) {
        gs.combat = { ...gs.combat, status: CombatStatus.defeat }
        notify(TOPICS.combat.ended, { victory: false })
        clearCombatLogicCache()
        gs.combat = null
        return true
      }
      return false
    }
    const finalizeCombatVictory = (): boolean => {
      if (!gs.combat || gs.combat.status !== CombatStatus.victory) return false
      const defeatedNames = new Set(
        listCombatFoes(gs.combat)
          .filter(foe => foe.vitality <= 0)
          .map(foe => foe.name)
      )
      gs.currentRoom.npcs = gs.currentRoom.npcs.filter(n => !defeatedNames.has(n.name))
      notify(TOPICS.combat.ended, { victory: true })
      clearCombatLogicCache()
      gs.combat = null
      return true
    }
    const runCompanionQueuedTurn = async (queuedCompanionName: string): Promise<boolean> => {
      if (!gs.combat || gs.combat.status !== CombatStatus.active) return false
      const companion = (gs.companions ?? []).find(c => c.name.toLowerCase() === queuedCompanionName.toLowerCase())
      if (!companion) {
        gs.combat = advanceCombatQueueCursor(gs.combat)
        return false
      }

      let queuedInput = `Attack ${getActiveTargetName(gs.combat)}`
      try {
        const choices = await getCompanionActions(gs, [companion])
        const selected = choices.find(c => c.name.toLowerCase() === companion.name.toLowerCase())?.action?.trim()
        if (selected) queuedInput = selected
      } catch (e) {
        console.warn('[combat-queue] companion autonomy fallback', e)
      }

      const queuedActor = resolveActionActor(gs, companion.name)
      const queuedActorState = projectLogicStateForActor(gs, queuedActor)
      const queuedAbilityInvocation = parseAbilityInvocation(queuedActorState, queuedInput)
      const key = combatLogicCacheKey(queuedActorState, queuedInput, queuedActor, queuedAbilityInvocation, {
        source: 'companion',
      })
      let queuedLogicFn = '(state, roll) => ({ successLevel: 0, updates: { timeCost: 5 } })'
      let queuedMeta: LogicForgeMeta = FALLBACK_META
      let queuedMechanics: PendingAction['mechanics']
      const cached = combatLogicCache.get(key)
      if (cached) {
        queuedLogicFn = cached.logicFn
        queuedMeta = cached.meta
        queuedMechanics = cached.mechanics ? { ...cached.mechanics } : undefined
      } else {
        const forged = await runLogicPhase(queuedActorState, queuedInput, companion.name)
        queuedLogicFn = forged.logicFnString
        queuedMeta = forged.meta
        queuedMechanics = forged.mechanics
        combatLogicCache.set(key, {
          logicFn: queuedLogicFn,
          meta: queuedMeta,
          mechanics: queuedMechanics ? { ...queuedMechanics } : undefined,
        })
      }

      const queuedSkill = getActionSkillContext(queuedActorState, queuedInput, queuedMeta.category)
      if (queuedMechanics) {
        queuedMechanics = resolveMechanicsBonus(queuedActorState, queuedMechanics, queuedSkill?.bonus ?? 0, {
          category: queuedMeta.category,
          input: queuedInput,
        })
      }

      const rawRoll = Math.floor(Math.random() * 20) + 1
      const queuedBonus = Number.isFinite(queuedMechanics?.bonus)
        ? Number(queuedMechanics?.bonus)
        : (queuedSkill?.bonus ?? 0)
      const queuedRoll = Math.max(1, Math.min(40, rawRoll + queuedBonus))
      const queuedEnvelope = buildActionEnvelope(gs, queuedInput, {
        source: 'companion',
        actor: { kind: 'companion', name: companion.name },
        category: queuedMeta.category,
        ability: queuedMeta.category === ActionCategory.ability
          ? parseAbilityInvocation(queuedActorState, queuedInput)?.ability
          : undefined,
      })
      const queuedContext = buildActionExecutionContext(gs, queuedEnvelope, queuedRoll, queuedMechanics)
      let queuedUpdate = await executeInWorker(queuedLogicFn, queuedActorState, queuedRoll, FALLBACK_LOGIC, false, queuedMechanics, queuedContext)
      queuedUpdate = {
        ...queuedUpdate,
        successLevel: reconcileSuccessLevelWithCheck(
          queuedUpdate.successLevel,
          queuedRoll,
          queuedMechanics ? { dc: queuedMechanics.dc } : undefined,
        ),
      }
      applyLogicUpdates(gs, queuedUpdate, queuedMeta, queuedInput, {
        emit: notify,
        actor: { kind: 'companion', name: companion.name },
      })
      let queuedStatVal = companion.attributes.STR?.val ?? gs.player.attributes.STR.val
      const queuedPrimary = queuedMechanics?.primaryStat
        ? asAttributeKey(queuedMechanics.primaryStat)
        : null
      if (queuedPrimary && companion.attributes[queuedPrimary]) {
        queuedStatVal = companion.attributes[queuedPrimary].val
      }

      const queuedAbility = queuedMeta.category === ActionCategory.ability
        ? parseAbilityInvocation(queuedActorState, queuedInput)?.ability
        : undefined
      const queuedOffensive = queuedMeta.category === ActionCategory.attack || (
        !!queuedAbility &&
        getResolvedAbilityArgs(queuedActorState, queuedAbility).some(arg => arg.type === 'target')
      )
      const activeTarget = getActiveTargetName(gs.combat)
      const { combat: afterCompanion } = processPlayerTurn(
        gs.combat,
        queuedInput,
        queuedUpdate.successLevel,
        queuedStatVal,
        queuedOffensive,
        'companion',
        companion.name,
        activeTarget,
      )
      gs.combat = afterCompanion
      const companionOutcome = gs.combat.log[gs.combat.log.length - 1]?.outcome ?? ''
      notify(TOPICS.combat.turnEnd, {
        round: gs.combat.round,
        actor: 'companion',
        actorName: companion.name,
        action: queuedInput,
        outcome: companionOutcome,
      })
      if (finalizeCombatVictory()) return true
      if (gs.combat) gs.combat = advanceCombatQueueCursor(gs.combat)
      return false
    }
    const runQueuedActorsUntilPlayer = async (): Promise<void> => {
      if (!gs.combat || gs.combat.status !== CombatStatus.active) return
      const queueLen = Math.max(1, gs.combat.turnQueue?.length ?? gs.combat.initiative.length ?? 1)
      const maxSteps = queueLen * 3
      for (let step = 0; step < maxSteps; step++) {
        if (!gs.combat || gs.combat.status !== CombatStatus.active) return
        const actor = String(peekNextCombatActor(gs.combat) ?? 'player').trim()
        if (!actor || actor.toLowerCase() === 'player') return
        const allySet = new Set((gs.combat.allies ?? []).map(v => String(v ?? '').trim().toLowerCase()))
        if (allySet.has(actor.toLowerCase())) {
          const ended = await runCompanionQueuedTurn(actor)
          if (ended) return
          continue
        }
        const defeated = await runNpcTurn()
        if (defeated) return
      }
    }
    if (isFlee(input) && gs.combat?.status === CombatStatus.active) {
      playerTurnInterrupted = true
      gs.combat = fleeCombat(gs.combat)
      notify(TOPICS.combat.fled)
      clearCombatLogicCache()
      gs.combat = null
    } else if (gs.combat?.status === CombatStatus.active || (isAttack && gs.currentRoom.npcs.length > 0)) {
      let requestedTarget = extractTargetName(input)
      const opponent = gs.combat?.npc ?? pickOpponent(gs.currentRoom.npcs, requestedTarget)
      if (opponent) {
        if (!gs.combat || gs.combat.status !== CombatStatus.active) {
          gs.combat = startCombat(
            opponent,
            gs.player,
            (gs.companions ?? []).map(c => c.name),
            (gs.currentRoom.npcs ?? []).map(n => n.name),
          )
          clearCombatLogicCache()
          notify(TOPICS.combat.started, { npc: opponent })
        }
        if (gs.combat && requestedTarget) {
          gs.combat = setCombatTarget(gs.combat, requestedTarget)
        }

        const npcGoesFirst = shouldNpcActFirst(gs.combat)
        if (npcGoesFirst) {
          const playerDefeatedBeforeAction = await runNpcTurn()
          if (playerDefeatedBeforeAction) {
            playerTurnInterrupted = true
            logicUpdate = {
              successLevel: 0,
              updates: { timeCost: Math.max(5, logicUpdate.updates.timeCost ?? 5) },
            }
          }
        }

        if (!gs.combat || gs.combat.status !== CombatStatus.active) {
          // Combat ended
        } else {
          applyLogicUpdates(gs, logicUpdate, meta, input, {
            emit: notify,
            actor: actorKind === 'companion'
              ? { kind: 'companion', name: actionActorName ?? companionName }
              : { kind: 'player' },
          })
          playerLogicApplied = true
          const activeTarget = getActiveTargetName(gs.combat)
          const { combat: afterPlayer } = processPlayerTurn(
            gs.combat,
            input,
            logicUpdate.successLevel,
            statVal,
            isOffensive,
            actorKind === 'companion' ? 'companion' : 'player',
            actionActorName ?? companionName,
            activeTarget,
          )
          gs.combat = afterPlayer
          if (gs.combat.status === CombatStatus.victory) {
            finalizeCombatVictory()
          } else {
            const playerOutcome = gs.combat.log[gs.combat.log.length - 1]?.outcome ?? ''
            notify(TOPICS.combat.turnEnd, {
              round: gs.combat.round,
              actor: actorKind === 'companion' ? 'companion' : 'player',
              actorName: actionActorName ?? companionName,
              action: input,
              outcome: playerOutcome,
            })
            if (gs.combat) gs.combat = advanceCombatQueueCursor(gs.combat)
            await runQueuedActorsUntilPlayer()
          }
        }
      }
    }

    if (!playerLogicApplied && !playerTurnInterrupted) {
      applyLogicUpdates(gs, logicUpdate, meta, input, {
        emit: notify,
        actor: actorKind === 'companion'
          ? { kind: 'companion', name: actionActorName ?? companionName }
          : { kind: 'player' },
      })
      playerLogicApplied = true
    }

    if (playerLogicApplied) {
      if (logicUpdate.successLevel === 2) playEffect('action_success')
      else if (logicUpdate.successLevel === 1) playEffect('action_partial')
      else playEffect('action_fail')
    } else {
      playEffect('action_fail')
    }

    const { updates } = logicUpdate
    const { entropyShiftTriggered } = advanceClock(gs, updates.timeCost ?? 15)
    applyDueScheduledEffectJobs(gs)
    if (entropyShiftTriggered && _hasEntropyTrait(gs.player.traits)) {
      const shifted = await runTraitShift(gs.player.bio)
      if (shifted.traits.length) gs.player.traits = shifted.traits
      notify(TOPICS.game.entropyShift, shifted)
      playEffect('entropy_shift')
    }

    const actorName = companionName || gs.player.name || gs.player.identity?.split(/\s+/).slice(0, 2).join(' ') || 'Player'
    const newHistoryEntry: GameState['history'][number] = {
      location: gs.currentRoom.name,
      time: formatClock(gs.world.clock),
      text: `**${actorName}**: ${input}\n\n`,
      suggestions: [],
      context: { items: [...gs.currentRoom.items], npcs: [...gs.currentRoom.npcs] },
    }
    gs.history.push(newHistoryEntry)
    notify(TOPICS.game.actionUpdate, { state: gs })
    let narrationFinished = false

    const onComplete = (narratorData: Omit<NarratorAIResponse, 'prose'>) => {
      newHistoryEntry.suggestions = narratorData.suggestions
      handleMovement(gs, input, narratorData.newRoom)
      const atlasSync = applyAtlasKnowledge(gs.atlas, narratorData.atlasHints)
      if ((atlasSync.placesAdded + atlasSync.pathsAdded) > 0) {
        addNotebookEntry(gs, `Map learned: ${atlasSync.placesAdded} place(s), ${atlasSync.pathsAdded} path(s).`, 'atlas')
      }
      const proseOnly = String(newHistoryEntry.text ?? '').split('\n\n').slice(1).join(' ').trim()
      const sentenceMatch = proseOnly.match(/^(.+?[.!?])(?:\s|$)/)
      const firstSentence = (sentenceMatch?.[1] ?? proseOnly).trim()
      if (firstSentence && shouldCaptureNarrationNotebook(firstSentence)) {
        addNotebookEntry(gs, `${gs.currentRoom.name}: ${firstSentence}`, 'event')
      }
      playEffect('narrator_chime')
      speakNarration(newHistoryEntry.text)
      sysLog('Ready.')
      narrationFinished = true
    }
    const stream = streamNarratorPhase(gs, input, logicUpdate, onComplete)
    for await (const proseChunk of stream) {
      newHistoryEntry.text += proseChunk
      notify(TOPICS.game.actionUpdate, { state: gs })
    }

    if (narrationFinished) {
      await save(true)
      isProcessing = false
      notify(TOPICS.game.actionComplete, { state: gs, actionId, isCompanionAction })
      updateScoreMood(gs)
      if (!isCompanionAction && gs.combat?.status !== CombatStatus.active) triggerAutonomy(gs)
    }

  } catch (e) {
    console.error('[Action] Resolution failed:', e)
    pendingAction = null
    isProcessing = false
    save()
    notify(TOPICS.game.actionFailed, { error: formatAIErrorMessage(e), actionId, isCompanionAction })
  }
}

function handleMovement(gs: GameState, input: string, newRoom?: NarratorAIResponse['newRoom']) {
  const inferredMovement = inferMovementFallback(gs, input)
  let movementRoom = newRoom ?? null
  if (movementRoom) {
    if (inferredMovement) {
      movementRoom = {
        ...movementRoom,
        xOff: inferredMovement.xOff,
        yOff: inferredMovement.yOff,
      }
    } else {
      const testX = movementRoom.xOff ?? 0
      const testY = movementRoom.yOff ?? 0
      const isNarratorMoveValid = Number.isInteger(testX) && Number.isInteger(testY) &&
        Math.abs(testX) <= 1 && Math.abs(testY) <= 1 &&
        (Math.abs(testX) + Math.abs(testY) === 1 || (testX === 0 && testY === 0))
      if (!isNarratorMoveValid) {
        movementRoom = inferredMovement || null
      }
    }
  } else {
    movementRoom = inferredMovement
  }

  if (movementRoom && !gs.combat) {
    const nr = movementRoom
    const xOff = nr.xOff || 0
    const yOff = nr.yOff || 0
    const zOff = nr.zOff || 0
    if ((xOff !== 0 || yOff !== 0 || zOff !== 0)) {
      const oldLocKey = `${gs.player.location.x}_${gs.player.location.y}_${gs.player.location.z ?? 0}`
      const oldNode = gs.atlas.nodes[oldLocKey]
      if (oldNode) {
        oldNode.items = [...gs.currentRoom.items]
        oldNode.flags = { ...gs.currentRoom.flags }
        oldNode.dormantFlags = [...(gs.currentRoom.dormantFlags ?? [])]
        oldNode.lastVisited = totalMinFromClock(gs.world.clock)
      }

      const newX = gs.player.location.x + xOff
      const newY = gs.player.location.y + yOff
      const newZ = (gs.player.location.z ?? 0) + zOff
      const newLocKey = `${newX}_${newY}_${newZ}`
      
      const travelConsequences = processTravelRisk(gs, gs.player.location.x, gs.player.location.y, gs.player.location.z ?? 0, newX, newY, newZ)
      if (travelConsequences.vitalityLoss > 0 || travelConsequences.eventTriggered) {
        notify(TOPICS.world.ticked, [{ 
          time: formatClock(gs.world.clock), 
          text: `Travel toll: -${travelConsequences.vitalityLoss} VIT. ${travelConsequences.eventTriggered || ''}`,
          type: 'environment' 
        }])
      }

      gs.player.location = { x: newX, y: newY, z: newZ }
      const existingNode = gs.atlas.nodes[newLocKey]
      if (existingNode) {
        gs.currentRoom = {
          name: existingNode.name,
          desc: existingNode.desc ?? '',
          items: existingNode.items ?? [],
          npcs: [],
          flags: existingNode.flags ?? {},
          dormantFlags: existingNode.dormantFlags ?? [],
          clocks: existingNode.clocks ?? [],
        }
      } else {
        gs.atlas.nodes[newLocKey] = { name: nr.name, desc: nr.desc, items: nr.items ?? [], dormantFlags: [], clocks: [] }
        gs.atlas.edges.push({ x1: parseInt(oldLocKey.split('_')[0]), y1: parseInt(oldLocKey.split('_')[1]), z1: parseInt(oldLocKey.split('_')[2]), x2: newX, y2: newY, z2: newZ })
        gs.currentRoom = {
          name: nr.name,
          desc: nr.desc,
          items: nr.items ?? [],
          npcs: sanitizeRoomNpcs(nr.npcs as GameState['currentRoom']['npcs']),
          dormantFlags: [],
          clocks: [],
        }
      }
      syncRoomNPCs()
      notify(TOPICS.game.roomChanged, gs.currentRoom)
      setAmbientSignature(gs.world?.seed || '', gs.currentRoom.desc || gs.currentRoom.name || '')
      playAmbient(inferAmbientScene(gs.currentRoom.desc || gs.currentRoom.name))
      triggerSceneMusicRefresh(gs.world?.seed || '', gs.currentRoom.desc || gs.currentRoom.name || '')
      addNotebookEntry(gs, `Discovered location: ${gs.currentRoom.name}`, 'location')
      if (gs.currentRoom.desc) addNotebookEntry(gs, `${gs.currentRoom.name}: ${gs.currentRoom.desc}`, 'location')
      
      const currentNode = gs.atlas.nodes[newLocKey]
      if (currentNode?.visionUrl) {
        gs.visionUrl = currentNode.visionUrl
        notify(TOPICS.game.imageReady, { url: gs.visionUrl, kind: 'vision' })
      } else {
        requestImageWithRetry(
          'vision',
          buildVisionPrompt(gs, gs.currentRoom),
          (v) => {
            if (!state) return
            state.visionUrl = v
            if (state.atlas.nodes[newLocKey]) state.atlas.nodes[newLocKey].visionUrl = v
            notify(TOPICS.game.imageReady, { url: v, kind: 'vision' })
          },
        )
      }
    }
  }
}

function triggerAutonomy(gs: GameState) {
  if (gs.companions?.length) {
    getCompanionActions(gs, gs.companions).then(actions => {
      let promiseChain = Promise.resolve()
      for (const cAction of actions) {
        promiseChain = promiseChain.then(() => handleAction(cAction.action, cAction.name))
      }
      return promiseChain
    }).then(() => {
      checkAndTick().catch(console.warn)
    }).catch(e => console.error('Companion autonomy failed:', e))
  } else {
    checkAndTick().catch(console.warn)
  }
}

export async function startNewGame(params: {
  name?: string
  identity: string
  lineage?: string
  sex?: string
  bio: string
  seed: string
  worldData: ImportedWorldData | null
  playerData: ImportedPlayerData | null
}): Promise<{ state: GameState; saveId: string }> {
  const { name, identity, lineage, sex, bio, seed, worldData, playerData } = params
  sysLog('Forging reality foundation...')
  const actualSeed = String(worldData?.seed ?? seed ?? '').trim()
  const taxonomy = await generateWorldTaxonomy(actualSeed)
  
  sysLog(`Weaving the Vessel for ${taxonomy.worldName}...`)
  const baseIdentity = String(identity ?? playerData?.identity ?? '').trim() || 'Unknown Vessel'
  const actualLineage = String(lineage ?? '').trim()
  const submittedIdentity = actualLineage ? `${baseIdentity} (${actualLineage})` : baseIdentity
  const actualBio = String(bio ?? playerData?.bio ?? '').trim() || 'A wandering soul shaped by strange worlds.'
  const genesisBio = [
    actualBio,
    actualLineage ? `Lineage/Species: ${actualLineage}.` : '',
    sex ? `Sex: ${sex}.` : '',
  ].filter(Boolean).join('\n')

  const genesis = await runGenesis(submittedIdentity, genesisBio, actualSeed, taxonomy)
  
  const canonicalIdentity = actualLineage
    ? submittedIdentity
    : normalizeGenesisIdentity(genesis.identity, submittedIdentity)
  const resolvedSex = resolveCreationSex(normalizeSex(sex) || normalizeSex(playerData?.sex), genesis.sex, `${actualSeed}|${canonicalIdentity}|${actualBio}`)

  const fresh = defaultGameState()
  fresh.player.name = name?.trim() || genesis.name?.trim() || undefined
  fresh.player.identity = canonicalIdentity
  fresh.player.bio = actualBio
  fresh.player.sex = resolvedSex
  fresh.player.traits = genesis.traits
  fresh.player.skills = normalizeSkills(genesis.skills, fresh.player.attributes)
  fresh.player.abilities = genesis.abilities ?? []
  fresh.player.inventory = (genesis.inventory ?? [])
    .map(name => String(name ?? '').trim())
    .filter(Boolean)
    .map(name => ({ name, equipped: false }))

  let remainingGenesisCompanionNodes = MAX_GENESIS_COMPANION_NODES
  let depthLimitWarned = false
  let sizeLimitWarned = false
  const parseCompanion = (raw: any, depth = 0): Companion | null => {
    if (!raw || typeof raw !== 'object') return null
    if (depth > MAX_GENESIS_COMPANION_DEPTH) {
      if (!depthLimitWarned) {
        depthLimitWarned = true
        console.warn('[Genesis] Companion nesting depth exceeded limit.')
      }
      return null
    }
    if (remainingGenesisCompanionNodes <= 0) {
      if (!sizeLimitWarned) {
        sizeLimitWarned = true
        console.warn('[Genesis] Companion tree exceeded node budget.')
      }
      return null
    }
    remainingGenesisCompanionNodes -= 1
    const name = String(raw.name ?? '').trim()
    const identity = String(raw.identity ?? '').trim()
    const bio = String(raw.bio ?? '').trim()
    if (!name || !identity || !bio) return null
    const attributes = flatStatsToAttributes(raw.attributes)
    const nestedCompanions = Array.isArray(raw.companions)
      ? raw.companions.map((child: any) => parseCompanion(child, depth + 1)).filter(Boolean) as Companion[]
      : []
    return {
      ...raw,
      name,
      identity,
      bio,
      sex: resolveCreationSex(undefined, raw.sex, `${actualSeed}|${raw.name}|${raw.identity}|${raw.bio}`),
      vitals: normalizeVitals(raw.vitals, attributes),
      attributes,
      skills: normalizeSkills(raw.skills, attributes),
      modifiers: {},
      dormantEffects: [],
      motives: Array.isArray(raw.motives) ? raw.motives : [],
      feelings: Array.isArray(raw.feelings) ? raw.feelings : [],
      traits: Array.isArray(raw.traits) ? raw.traits : [],
      abilities: Array.isArray(raw.abilities) ? raw.abilities : [],
      inventory: (raw.inventory ?? [])
        .map((name: any) => String(name ?? '').trim())
        .filter(Boolean)
        .map((name: any) => ({ name, equipped: false })),
      conversations: [],
      companions: nestedCompanions.length ? nestedCompanions : undefined,
    }
  }
  fresh.companions = (genesis.companions ?? [])
    .map((companion: any) => parseCompanion(companion, 0))
    .filter(Boolean) as Companion[]
  fresh.world.seed = actualSeed
  fresh.world.taxonomy = taxonomy
  fresh.meta.name = canonicalIdentity

  if (genesis.quests?.length) {
    const totalMin = totalMinFromClock(fresh.world.clock)
    fresh.journal = genesis.quests.map(q => ({ ...q, created: totalMin }))
  }

  for (const [k, v] of Object.entries(genesis.stats)) {
    const key = asAttributeKey(k)
    if (!key) continue
    if (fresh.player.attributes[key]) fresh.player.attributes[key].val = v
  }
  fresh.player.vitals = fullVitals(fresh.player.attributes)

  if (playerData) {
    const imported = normalizeImportedPlayerData(playerData)
    if (imported) {
      const attributes = normalizeAttributeMetadata(imported.attributes ?? {})
      fresh.player.identity = canonicalIdentity
      fresh.player.bio = actualBio
      fresh.player.sex = resolveCreationSex(normalizeSex(sex) || normalizeSex(playerData?.sex), imported.sex, `${actualSeed}|${canonicalIdentity}|${actualBio}`)
      fresh.player.attributes = attributes
      fresh.player.vitals = normalizeVitals(imported.vitals, attributes, fresh.player.vitals)
      fresh.player.skills = normalizeSkills(imported.skills, attributes)
      fresh.player.abilities = structuredClone(imported.abilities ?? [])
      fresh.player.traits = structuredClone(imported.traits ?? [])
      fresh.player.inventory = (imported.inventory ?? [])
        .map((item: any) => {
          const name = String(item?.name ?? '').trim()
          if (!name) return null
          return { name, equipped: !!item?.equipped }
        })
        .filter(Boolean) as GameState['player']['inventory']
      fresh.meta.name = canonicalIdentity
    }
  }

  if (worldData) {
    fresh.atlas = worldData.atlas
    fresh.currentRoom = {
      ...worldData.room,
      npcs: sanitizeRoomNpcs(worldData.room.npcs),
      dormantFlags: worldData.room.dormantFlags ?? [],
      clocks: worldData.room.clocks ?? [],
    }
    fresh.meta.origin = playerData ? GameOrigin.imported_starter : GameOrigin.imported_blueprint
    fresh.history = [{
      location: fresh.currentRoom.name,
      time: formatClock(fresh.world.clock),
      text: playerData
        ? 'You have arrived in a shared reality with a carried vessel profile.'
        : 'You have arrived in a shared reality.',
      suggestions: [],
      context: { items: [...fresh.currentRoom.items], npcs: [...fresh.currentRoom.npcs] },
    }]
  } else {
    fresh.currentRoom = {
      name: genesis.room.name,
      desc: genesis.room.desc,
      items: genesis.room.items,
      npcs: sanitizeRoomNpcs(genesis.room.npcs),
      dormantFlags: [],
      clocks: [],
    }
    const starterAtlas = buildStarterAtlasScaffold(actualSeed, genesis.room.name, genesis.room.desc)
    fresh.atlas.nodes = starterAtlas.nodes
    fresh.atlas.edges = starterAtlas.edges
    if (playerData) fresh.meta.origin = GameOrigin.imported_vessel
    fresh.history = [{
      location: genesis.room.name,
      time: formatClock(fresh.world.clock),
      text: genesis.room.narration,
      suggestions: genesis.room.suggestions,
      context: { items: [...fresh.currentRoom.items], npcs: [...fresh.currentRoom.npcs] },
    }]
  }

  const seedLine = String(actualSeed ?? '').split('\n')[0].trim()
  if (seedLine) addNotebookEntry(fresh, `World premise: ${seedLine}`, 'world')
  addNotebookEntry(fresh, `Starting location: ${fresh.currentRoom.name}`, 'location')
  if (fresh.currentRoom.desc) addNotebookEntry(fresh, `${fresh.currentRoom.name}: ${fresh.currentRoom.desc}`, 'location')
  for (const companion of (fresh.companions ?? [])) {
    addNotebookEntry(fresh, `Companion: ${companion.name} — ${companion.identity}`, 'people')
  }

  const u = uid()
  pendingAction = null
  clearCombatLogicCache()
  isProcessing = false
  state = fresh
  activeSaveId = Date.now().toString()
  const saveId = activeSaveId
  lastSavedFingerprint = null
  importedWorldData = null
  importedPlayerData = null

  if (u) {
    const [{ startPendingSaveSync }, { initLogicCache }, { initNpcLogicCache }] = await Promise.all([
      loadPersistRuntime(),
      loadLogicCacheRuntime(),
      loadNpcLogicCacheRuntime(),
    ])
    initLogicCache(u, saveId).catch(console.warn)
    initNpcLogicCache(u)
    startPendingSaveSync(u)
  }

  await save(true)
  setWorldSimState(state)
  seedWorldNPCs()
  startWorldTick()
  scheduleEnrichmentIfNeeded()
  setAmbientSignature(state.world?.seed || '', state.currentRoom.desc || state.currentRoom.name || '')
  playAmbient(inferAmbientScene(state.currentRoom.desc || state.currentRoom.name))
  notify(TOPICS.game.started, state)
  clearDynamicScores()
  triggerMusicGeneration(actualSeed, fresh.currentRoom.desc || fresh.currentRoom.name || '')

  if (!worldData) {
    const scaffoldKeys = Object.keys(fresh.atlas.nodes).filter(k => k !== '0_0_0')
    if (scaffoldKeys.length) {
      runWorldForge(actualSeed, genesis.room.name, scaffoldKeys).then(forgedNodes => {
        if (!state) return
        for (const fn of forgedNodes) {
          const existing = state.atlas.nodes[fn.key]
          if (!existing) continue
          existing.name = fn.name || existing.name
          existing.desc = fn.desc || existing.desc
          existing.items = fn.items.length ? fn.items : existing.items
          if (fn.spawns?.length) {
            existing.spawns = fn.spawns
            existing.lastSpawnCheck = totalMinFromClock(state.world.clock)
          }
          for (const npc of fn.npcs) {
            const id = generateNpcId(npc.name)
            if (state.world.npcs.some(n => n.id === id)) continue
            state.world.npcs.push({
              id,
              name: npc.name,
              identity: npc.identity,
              lore: npc.lore,
              sex: normalizeSex(npc.sex),
              intentions: npc.intentions,
              feelings: {},
              location: fn.key,
              lastActed: 0,
              relationship: npc.relationship,
              memory: npc.memory,
              inventory: npc.inventory,
              activeEffects: [],
              dormantEffects: [],
            })
          }
        }
        save().catch(console.warn)
      }).catch(e => console.warn('[WorldForge] Background forge failed:', e))
    }
  }

  sysLog('Reality online. Generating visuals...')
  requestImageWithRetry(
    'vision',
    buildVisionPrompt(fresh, fresh.currentRoom),
    (v) => {
      if (!state) return
      state.visionUrl = v
      cacheVisionOnCurrentNode(state, v)
      notify(TOPICS.game.imageReady, { url: v, kind: 'vision' })
    },
  )

  requestImageWithRetry(
    'portrait',
    buildPortraitPrompt({
      name: fresh.player.name,
      identity: fresh.player.identity,
      sex: fresh.player.sex,
      bio: genesisBio,
    }),
    (p) => {
      if (!state) return
      state.portraitUrl = p
      notify(TOPICS.game.imageReady, { url: p, kind: 'portrait' })
    },
  )

  const companionPortraitQueue = fresh.companions
    .filter(c => !!c.bio)
    .map(c => ({ name: c.name, identity: c.identity, sex: c.sex, bio: c.bio }))
  queueCompanionPortraitGeneration(companionPortraitQueue)
  sysLog('Reality online.')
  return { state, saveId }
}

// --- Import ---

export async function execImport(code: string): Promise<{
  type: 'blueprint' | 'scenario' | 'vessel' | 'starter' | 'not_found'
  worldData?: ImportedWorldData
  playerData?: ImportedPlayerData
}> {
  sysLog('Querying Public Registry...')
  const { importByCode, startPendingSaveSync } = await loadPersistRuntime()
  const result = await importByCode(code.toUpperCase())

  if (result.type === 'not_found') {
    sysLog('CODE INVALID OR NETWORK ERROR.')
    return { type: 'not_found' }
  }

  if (result.type === 'blueprint') {
    importedWorldData = normalizeImportedWorldData(result.data)
    importedPlayerData = null
    if (!importedWorldData) {
      sysLog('World blueprint payload invalid.')
      return { type: 'not_found' }
    }
    sysLog('World blueprint acquired. Configure your Vessel.')
    return { type: 'blueprint', worldData: importedWorldData }
  }

  if (result.type === 'vessel') {
    const profile = normalizeImportedPlayerData(result.data)
    if (!profile) {
      sysLog('Vessel profile payload invalid.')
      return { type: 'not_found' }
    }
    importedPlayerData = profile
    importedWorldData = null
    sysLog('Vessel profile acquired. Configure world and launch.')
    return { type: 'vessel', playerData: profile }
  }

  if (result.type === 'starter') {
    const starter = result.data as SharedStarterKit
    const profile = normalizeImportedPlayerData(starter?.player)
    if (!profile || !starter?.seed || !starter?.atlas || !starter?.room) {
      sysLog('Starter kit payload invalid.')
      return { type: 'not_found' }
    }
    importedWorldData = normalizeImportedWorldData({
      seed: String(starter.seed),
      atlas: starter.atlas,
      room: starter.room,
    })
    if (!importedWorldData) {
      sysLog('Starter kit world payload invalid.')
      return { type: 'not_found' }
    }
    importedPlayerData = profile
    sysLog('Starter kit acquired. Ready to initiate.')
    return { type: 'starter', worldData: importedWorldData, playerData: profile }
  }

  if (result.type === 'scenario') {
    const u = uid()
    result.state.meta.origin = GameOrigin.imported_scenario
    result.state.meta.name = `${result.state.player.identity} (Imported)`
    result.state.combat = null
    pendingAction = null
    clearCombatLogicCache()
    isProcessing = false
    state = result.state
    activeSaveId = Date.now().toString()
    const saveId = activeSaveId
    if (u) {
      const [{ startPendingSaveSync }, { initLogicCache }, { initNpcLogicCache }] = await Promise.all([
        loadPersistRuntime(),
        loadLogicCacheRuntime(),
        loadNpcLogicCacheRuntime(),
      ])
      initLogicCache(u, saveId).catch(console.warn)
      initNpcLogicCache(u)
      startPendingSaveSync(u)
    }
    await save(true)
    setWorldSimState(state)
    seedWorldNPCs()
    startWorldTick()
    scheduleEnrichmentIfNeeded()
    setAmbientSignature(state.world?.seed || '', state.currentRoom.desc || state.currentRoom.name || '')
    playAmbient(inferAmbientScene(state.currentRoom.desc || state.currentRoom.name))
    notify(TOPICS.game.loaded, state)
    sysLog('Scenario imported.')
    return { type: 'scenario' }
  }

  return { type: 'not_found' }
}

export async function deleteGame(id: string): Promise<void> {
  const { deleteGame: coreDelete } = await loadPersistRuntime()
  const owners = await getSaveOwnerCandidates(uid())
  if (!owners.length) return
  await Promise.all(owners.map((owner) => coreDelete(owner, id).catch(() => undefined)))
  if (activeSaveId === id) {
    state = null
    activeSaveId = null
  }
}

export async function loadWorldSnapshot(id: string): Promise<GameState | null> {
  const { loadGame: coreLoad } = await loadPersistRuntime()
  const owner = await findSaveOwner(id, uid())
  if (!owner) return null
  return await coreLoad(owner, id)
}

export async function exportActiveSaveBackup(): Promise<string | null> {
  if (!activeSaveId) return null
  const { exportSaveBackup } = await loadPersistRuntime()
  const owner = await findSaveOwner(activeSaveId, uid())
  if (!owner) return null
  return await exportSaveBackup(owner, activeSaveId)
}

export async function importSaveBackupJson(raw: string): Promise<string | null> {
  const { importSaveBackup } = await loadPersistRuntime()
  const owner = await getSaveWriteOwner(uid())
  if (!owner) return null
  return await importSaveBackup(owner, raw)
}

export async function generateShare(type: string): Promise<string> {
  const { generateShareCode } = await loadPersistRuntime()
  return generateShareCode()
}

export async function talkToCompanion(companionName: string, text: string): Promise<void> {
  if (!state || isProcessing) return
  isProcessing = true
  try {
    const comp = state.companions.find(c => c.name === companionName)
    if (!comp) throw new Error('Companion not found')
    
    // In a real implementation, this would trigger logicForge runCompanionChat
    // For now, let's use the dispatcher to signal the start
    notify(TOPICS.game.actionStart, { input: text, isCompanionAction: true, companionName })
    
    const result = await runCompanionChat(state, comp, text)
    // Apply feelings if any
    const anyResult = result as any
    if (anyResult.feelings) {
      comp.feelings = { ...(comp.feelings || {}), ...anyResult.feelings }
    }
    
    const entry: GameState['history'][number] = {
      location: state.currentRoom.name,
      time: formatClock(state.world.clock),
      text: `**${state.player.name || 'You'}** (to ${comp.name}): ${text}\n\n${anyResult.prose || result}`,
      suggestions: anyResult.suggestions || [],
      context: { items: [...state.currentRoom.items], npcs: [...state.currentRoom.npcs] },
    }
    state.history.push(entry)
    
    notify(TOPICS.game.actionUpdate, { state })
    notify(TOPICS.game.actionComplete, { state, isCompanionAction: true })
    await save(true)
  } finally {
    isProcessing = false
  }
}

export async function getSaveList(): Promise<SaveMeta[]> {
  return await listAccessibleSaves(uid())
}

export async function getWorldGallery(): Promise<WorldGalleryEntry[]> {
  const saves = await listAccessibleSaves(uid())
  return saves
    .filter(s => !!s.state.world.seed)
    .map(s => ({
      saveId: s.id,
      seed: s.state.world.seed,
      roomName: s.state.currentRoom.name,
      saveName: s.state.meta.name,
      playerIdentity: s.state.player.identity,
      origin: s.state.meta.origin,
      timestamp: s.state.meta.timestamp,
    }))
    .sort((a, b) => b.timestamp - a.timestamp)
}

export async function getVesselGallery(): Promise<VesselGalleryEntry[]> {
  const saves = await listAccessibleSaves(uid())
  
  const entries: VesselGalleryEntry[] = []
  for (const s of saves) {
    const p = s.state.player
    entries.push({
      key: `${s.id}:${p.identity}`,
      identity: p.identity,
      bio: p.bio,
      sex: p.sex,
      portraitUrl: s.state.portraitUrl,
      saveName: s.state.meta.name,
      roomName: s.state.currentRoom.name,
      timestamp: s.state.meta.timestamp,
      origin: s.state.meta.origin,
      abilityCount: p.abilities?.length ?? 0,
      skillCount: p.skills?.length ?? 0,
      traitCount: p.traits?.length ?? 0,
      playerData: p,
    })
  }
  return entries.sort((a, b) => b.timestamp - a.timestamp)
}

export async function importWorldFromSave(saveId: string): Promise<ImportedWorldData | null> {
  const u = uid()
  if (!u) return null
  const { loadGame } = await loadPersistRuntime()
  const game = await loadGame(u, saveId)
  if (!game) return null
  
  const data = {
    seed: game.world.seed,
    atlas: game.atlas,
    room: game.currentRoom,
  }
  importedWorldData = data
  return data
}

export function getActiveSaveId(): string | null {
  return activeSaveId
}

export function isGameStoreProcessing(): boolean {
  return isProcessing
}
